import { useLayoutEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography, makeStyles } from '@material-ui/core';
import { BAR_COLOR } from './palette';

// The columns are set as close together as marks are allowed to be, so that as
// many of them as possible fit before any have to be dropped. What separates one
// bar from the next is mostly the room either side of it inside its own column,
// not this.
const GAP = 2;
const LABEL_LINES = 2;

// Two sizes of the same chart: the one a dashboard is read at, and the one a
// slide is read at from across a room. `minColumn` is the width below which a
// tag name is cut to nothing, so columns narrower than it are dropped instead;
// `charWidth` is roughly what one character of the label font is worth, which is
// what the name is broken into lines against.
const SIZES = {
    normal: { plotHeight: 140, minColumn: 78, fontSize: 11, charWidth: 6.1, barWidth: 44 },
    large: { plotHeight: 180, minColumn: 76, fontSize: 14, charWidth: 7.3, barWidth: 54 }
};

const useStyles = makeStyles(theme => ({
    plot: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: GAP,
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    column: {
        flex: '1 1 0',
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end'
    },
    // the bar is capped well short of its column: a thin mark beside a label the
    // full width of the column reads better than a block
    bar: {
        width: '100%',
        borderRadius: '4px 4px 0 0'
    },
    labels: {
        display: 'flex',
        gap: GAP,
        paddingTop: theme.spacing(1)
    },
    labelColumn: {
        flex: '1 1 0',
        minWidth: 0,
        textAlign: 'center'
    },
    // a name takes two lines whether it needs them or not, so that every column's
    // notes line up with its neighbours'
    name: {
        lineHeight: 1.25
    },
    nameLine: {
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden'
    }
}));

// Breaks a label into lines between its words - never inside one. A word too
// long for the column on its own is the only thing that is cut, and it is cut
// with an ellipsis rather than spilled onto the next line.
function labelLines(label, maxChars) {
    const clip = word => word.length <= maxChars ? word : `${word.slice(0, Math.max(1, maxChars - 1))}…`;
    const words = label.split(' ');
    const lines = [];
    words.forEach(word => {
        const last = lines[lines.length - 1];
        if (last != null && last.length + 1 + word.length <= maxChars) {
            lines[lines.length - 1] = `${last} ${word}`;
        } else {
            lines.push(word);
        }
    });
    if (lines.length <= LABEL_LINES) return lines.map(clip);
    // more words than there are lines: the rest are packed onto the last one and
    // cut there
    const kept = lines.slice(0, LABEL_LINES - 1);
    kept.push(lines.slice(LABEL_LINES - 1).join(' '));
    return kept.map(clip);
}

// How wide the chart is, and how many columns that is enough for
function useFittingCount(count, minColumn) {
    const container = useRef(null);
    const [measured, setMeasured] = useState({ width: null, fitting: count });
    useLayoutEffect(() => {
        const element = container.current;
        if (element == null) return;
        const measure = () => {
            const width = element.clientWidth;
            if (width === 0) return;
            setMeasured({ width, fitting: Math.max(1, Math.floor((width + GAP) / (minColumn + GAP))) });
        };
        measure();
        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [minColumn]);
    return [container, Math.min(measured.fitting, count), measured.width];
}

// Vertical bars, the height of each one read against the largest value of the
// set. The value is written over every column, so the columns stay readable
// without their heights being compared.
function ColumnChart(props) {
    const classes = useStyles();
    const size = props.large === true ? SIZES.large : SIZES.normal;
    const all = props.items ?? [];
    const [container, shownCount, width] = useFittingCount(all.length, size.minColumn);
    const items = all.slice(0, shownCount);
    const hidden = all.length - items.length;
    const max = Math.max(...items.map(item => item.value), 0);
    const columnWidth = width == null ? size.minColumn : (width - GAP * (shownCount - 1)) / shownCount;
    const maxChars = Math.max(3, Math.floor(columnWidth / size.charWidth));
    return (
        <div ref={container}>
            {all.length === 0 ?
                <Typography color='textSecondary'> {props.emptyMessage ?? "No data"} </Typography> :
                <>
                    <div className={classes.plot}>
                        {items.map(item => (
                            <Tooltip key={item.key} title={item.tooltip ?? ''} placement='top' arrow>
                                <div className={classes.column} style={{ height: size.plotHeight + 22 }}>
                                    <Typography variant='caption' style={{ fontSize: size.fontSize }}>
                                        {item.valueLabel}
                                    </Typography>
                                    <div
                                        className={classes.bar}
                                        style={{
                                            height: max === 0 ? 0 : Math.max(Math.round(item.value / max * size.plotHeight), item.value > 0 ? 3 : 0),
                                            maxWidth: size.barWidth,
                                            backgroundColor: item.color ?? BAR_COLOR
                                        }}
                                    />
                                </div>
                            </Tooltip>
                        ))}
                    </div>
                    <div className={classes.labels}>
                        {items.map(item => (
                            <div key={item.key} className={classes.labelColumn}>
                                <div
                                    className={classes.name}
                                    style={{ fontSize: size.fontSize, height: LABEL_LINES * 1.25 * size.fontSize }}
                                >
                                    {labelLines(item.label, maxChars).map(line =>
                                        <span key={line} className={classes.nameLine}> {line} </span>
                                    )}
                                </div>
                                {item.note &&
                                    <Typography variant='caption' color='textSecondary' style={{ fontSize: size.fontSize }}>
                                        {item.note}
                                    </Typography>
                                }
                            </div>
                        ))}
                    </div>
                    {hidden > 0 &&
                        <Box textAlign='right' pt={1}>
                            <Typography variant='caption' color='textSecondary'>
                                {hidden} more not shown
                            </Typography>
                        </Box>
                    }
                </>
            }
        </div>
    );
}

export default ColumnChart;
